import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PRICING_PAGE, BRAND } from "@/lib/constants";
import { Check, CalendarCheck, LockOpen, UsersRound } from "lucide-react";

const iconMap = { CalendarCheck, LockOpen, UsersRound };

export default function PricingCard() {
  const { card } = PRICING_PAGE;

  return (
    <section
      data-testid="pricing-card-section"
      className="bg-[#F9FAFB] py-12 md:py-20"
      aria-label="Pricing"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-xl">
        <div
          data-testid="pricing-main-card"
          className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
        >
          {/* Amber top border */}
          <div className="h-1 bg-[#D4A84B]" />

          <div className="p-6 md:p-10">
            {/* Label */}
            <p className="text-xs font-bold uppercase tracking-widest text-[#D4A84B] mb-6">
              {card.label}
            </p>

            {/* Price */}
            <div className="mb-1">
              <span
                data-testid="pricing-amount"
                className="font-heading text-5xl md:text-6xl font-extrabold text-[#1A1A1A]"
              >
                {card.price}
              </span>
              <span className="text-xl text-[#6B7280] font-medium ml-1">
                {card.priceSuffix}
              </span>
            </div>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm text-[#6B7280]">{card.currency}</span>
              <span className="text-xs text-[#6B7280]">{card.gst}</span>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 my-6" />

            {/* Features checklist - 2 columns desktop, 1 mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-6">
              {card.featuresCol1.map((f, i) => (
                <div
                  key={f}
                  className="flex items-center gap-2.5"
                  data-testid={`pricing-feature-col1-${i}`}
                >
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#D8FFB8] shrink-0">
                    <Check className="h-3 w-3 text-[#20611B]" />
                  </span>
                  <span className="text-sm text-[#1A1A1A]">{f}</span>
                </div>
              ))}
              {card.featuresCol2.map((f, i) => (
                <div
                  key={f}
                  className="flex items-center gap-2.5"
                  data-testid={`pricing-feature-col2-${i}`}
                >
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#D8FFB8] shrink-0">
                    <Check className="h-3 w-3 text-[#20611B]" />
                  </span>
                  <span className="text-sm text-[#1A1A1A]">{f}</span>
                </div>
              ))}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 my-6" />

            {/* Selling points */}
            <div className="space-y-3 mb-8">
              {card.sellingPoints.map((sp, i) => {
                const Icon = iconMap[sp.icon];
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3"
                    data-testid={`selling-point-${i}`}
                  >
                    {Icon && <Icon className="h-5 w-5 text-[#20611B] shrink-0" />}
                    <span className="text-sm text-[#1A1A1A] font-medium">{sp.text}</span>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <Link to={BRAND.signupPath}>
              {/* Stripe checkout will connect here */}
              <Button
                data-testid="pricing-card-cta"
                className="w-full bg-[#D4A84B] hover:bg-[#B88E3B] text-white font-bold h-14 rounded-md shadow-md hover:shadow-lg transition-all active:scale-95 text-base tracking-wide"
              >
                {card.cta}
              </Button>
            </Link>
            <p className="text-xs text-[#6B7280] text-center mt-3">
              {card.ctaSubtext}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
